import { FC, useEffect, useState } from 'react';
import { LabeledValue } from '../../account-info/AccountInfo';
import Loader from '../../loader/Loader';
import { doesStripeAccountExist, getStripeAccountStatus } from '../../../api/stripeConnectApi';
import { StripeAccountStatus } from '../../../types/interfaces/stripeConnect.interfaces';
import { LABEL_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../../constants/common';
import './billVendorInformation.scss';

interface BillVendorInfoProps {
  accountId: number;
}

const formatOnboardingStatus = (status?: string) => status?.replace(/([a-z])([A-Z])/g, '$1 $2') || '--';
const formatYesNo = (value?: boolean) => (value ? LABEL_CONSTANTS.YES : LABEL_CONSTANTS.NO);

const BillVendorInfo: FC<BillVendorInfoProps> = ({ accountId }) => {
  const [stripeStatus, setStripeStatus] = useState<StripeAccountStatus | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    let isCurrent = true;

    const fetchStripeAccountInfo = async () => {
      if (!accountId) return;

      setLoading(true);
      try {
        const { data: exists } = await doesStripeAccountExist(accountId);
        if (!isCurrent) return;

        if (exists !== true) {
          setStripeStatus(undefined);
          return;
        }

        const { data } = await getStripeAccountStatus(accountId);
        if (isCurrent) setStripeStatus(data);
      } catch {
        if (isCurrent) setStripeStatus(undefined);
      } finally {
        if (isCurrent) setLoading(false);
      }
    };

    setStripeStatus(undefined);
    void fetchStripeAccountInfo();

    return () => {
      isCurrent = false;
    };
  }, [accountId]);

  return (
    <div className="bill-vendor-info">
      <div className="header">
        <h2>{LABEL_CONSTANTS.STRIPE_ACCOUNT}</h2>
      </div>
      {loading ? (
        <Loader loaderSize={'small'} />
      ) : stripeStatus ? (
        <div className="body">
          <LabeledValue label={LABEL_CONSTANTS.STRIPE_ACCOUNT_ID} value={stripeStatus.stripeAccountId || '--'} />
          <LabeledValue label={LABEL_CONSTANTS.ONBOARDING_STATUS} value={formatOnboardingStatus(stripeStatus.onboardingStatus)} />
          <LabeledValue label={LABEL_CONSTANTS.PAYOUTS_ENABLED} value={formatYesNo(stripeStatus.payoutsEnabled)} />
          <LabeledValue label={LABEL_CONSTANTS.DETAILS_SUBMITTED} value={formatYesNo(stripeStatus.detailsSubmitted)} />
          <LabeledValue label={TABLE_HEADER_CONSTANTS.EMAIL_ADDRESS} value={stripeStatus.email || '--'} />
          <LabeledValue label={LABEL_CONSTANTS.PAYOUT_SCHEDULE} value={stripeStatus.payoutSchedule?.interval || '--'} />
        </div>
      ) : (
        <div className="no-association-message">
          {LABEL_CONSTANTS.STRIPE_ACCOUNT_NOT_FOUND}
          <br />
        </div>
      )}
    </div>
  );
};

export default BillVendorInfo;
